import axios from "axios";
import { API_CONFIG, getAuthHeaders, CORS_CONFIG } from "../config/api";

// Debug API configuration
console.log("🚀 API Service Debug:");
console.log("API_CONFIG.BASE_URL:", API_CONFIG.BASE_URL);
console.log("Full API_CONFIG:", API_CONFIG);

// Configure axios for live dashboard - Fixed configuration
const api = axios.create({
  baseURL: `${API_CONFIG.BASE_URL}/api`, // Add /api prefix to base URL
  timeout: 30000,
  withCredentials: true, // Disable for CORS issues in live environment
  headers: {
    "Content-Type": "application/json",
  },
});

console.log("🔥 Final API base URL:", `${API_CONFIG.BASE_URL}/api`);

// Request interceptor for live dashboard authentication
api.interceptors.request.use(
  (config) => {
    // Add auth headers for each request
    const authHeaders = getAuthHeaders();
    config.headers = { ...config.headers, ...authHeaders };

    // Log API calls for debugging
    console.log(
      `🌐 API Call: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`,
    );
    console.log(`📊 Request Headers:`, config.headers);
    console.log(`📊 Request Data:`, config.data);

    return config;
  },
  (error) => {
    console.error("❌ Request interceptor error:", error);
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Defensive: check if response.data is valid JSON object
    if (!response.data || typeof response.data !== "object") {
      throw new Error("Invalid API response format");
    }
    console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    console.log(`📊 Response Data:`, response.data);
    return response;
  },
  (error) => {
    // Defensive error handling
    let message = "Unknown error";
    // Try to extract a meaningful message
    if (
      error &&
      error.response &&
      error.response.data &&
      typeof error.response.data === "object" &&
      error.response.data.message
    ) {
      message = error.response.data.message;
    } else if (error && typeof error.message === "string" && error.message) {
      message = error.message;
    } else if (
      error &&
      error.response &&
      typeof error.response.data === "string" &&
      error.response.data
    ) {
      // If backend returns HTML or plain text
      message = error.response.data;
    } else if (error && error.toString) {
      message = error.toString();
    }
    // Log error details for debugging
    console.error("❌ API Error:", error?.response?.status, message, error);
    // Handle different error scenarios
    if (error?.response?.status === 401) {
      localStorage.removeItem("authToken");
      window.location.href = "/login";
    } else if (error?.response?.status === 403) {
      console.warn("License validation failed");
    } else if (
      error?.code === "ECONNREFUSED" ||
      error?.code === "ERR_NETWORK"
    ) {
      console.error(
        "🔴 Local API connection failed. Make sure the bot is running on your local machine.",
      );
    }
    // Never call substring or any string method on null/undefined
    return Promise.reject(new Error(message));
  },
);

export const apiService = {
  // ===== HEALTH CHECK FIRST =====

  healthCheck: () => {
    console.log("🏥 Health check API call");
    return api.get("/health");
  },

  // Simple test endpoint
  testConnection: () => {
    console.log("🔗 Testing connection...");
    return api.get("/test");
  },

  // ===== BOT OPERATIONS =====

  getBotStatus: () => api.get("/bot/status"),

  startBot: () => api.post("/bot/start"),

  stopBot: () => api.post("/bot/stop"),

  getBotLogs: (page = 1, limit = 50) =>
    api.get("/bot/logs", { params: { page, limit } }),

  getSessionHistory: (page = 1, limit = 20) =>
    api.get("/bot/sessions", { params: { page, limit } }),

  emergencyStop: () => api.post("/bot/emergency-stop"),

  // ===== SETTINGS =====

  getAllSettings: () => api.get("/settings/all"),

  // Health check for settings
  settingsHealthCheck: () => api.get("/settings/health"),

  // Search Keywords
  getKeywords: () => api.get("/settings/keywords"),

  addKeyword: (keywordData) => api.post("/settings/add-keyword", keywordData),

  updateKeyword: (id, keywordData) =>
    api.put(`/settings/keywords/${id}`, keywordData),

  deleteKeyword: (id) => api.delete(`/settings/keywords/${id}`),

  toggleKeywordStatus: (id) =>
    api.patch(`/settings/keywords/${id}/toggle-status`),

  // Alternative Messages
  getAlternativeMessages: (id) =>
    api.get(`/settings/keywords/${id}/alternative-messages`),

  addAlternativeMessage: (id, messageData) =>
    api.post(`/settings/keywords/${id}/alternative-messages`, messageData),

  deleteAlternativeMessage: (id, messageData) =>
    api.delete(`/settings/keywords/${id}/alternative-messages`, {
      data: messageData,
    }),

  // Daily Limits
  getDailyLimits: () => api.get("/settings/limits"),

  updateDailyLimits: (limitsData) => api.put("/settings/limits", limitsData),

  // Message Strategy
  getMessageStrategy: () => api.get("/settings/message-strategy"),

  updateMessageStrategy: (strategyData) =>
    api.put("/settings/message-strategy", strategyData),

  // Warmup Settings
  getWarmupSettings: () => api.get("/settings/warmup"),

  updateWarmupSettings: (warmupData) => api.put("/settings/warmup", warmupData),

  // ===== USER MANAGEMENT =====

  getUsers: () => api.get("/settings/users"),

  createUser: (userData) => api.post("/settings/users", userData),

  // ===== LICENSE =====

  getLicenseInfo: () => {
    console.log("🔍 Making license info API call...");
    return api.get("/license/info");
  },

  upgradeLicense: (upgradeData) => api.post("/license/upgrade", upgradeData),

  extendLicense: (days) => api.post("/license/extend", { days }),

  getUsageStats: () => api.get("/license/usage"),

  resetUsage: () => api.post("/license/reset-usage"),

  checkPermission: (action) =>
    api.post("/license/check-permission", { action }),

  // ===== ANALYTICS =====

  getDashboardAnalytics: (period = "7d") =>
    api.get("/analytics/dashboard", { params: { period } }),

  getActivityLogs: (params) => api.get("/analytics/activities", { params }),

  getPerformanceReport: (period = "30d") =>
    api.get("/analytics/performance", { params: { period } }),

  getKeywordAnalytics: (period = "30d") =>
    api.get("/analytics/keywords", { params: { period } }),

  exportData: (format = "json", period = "30d") =>
    api.get("/analytics/export", {
      params: { format, period },
      responseType: "blob",
    }),

  // ===== FOLLOW-UP ACCOUNTS =====

  getFollowUpLikeAccounts: (params) =>
    api.get("/followup/like-accounts", { params }),

  likeFollowUpAccount: (accountId, postId) =>
    api.post(`/followup/like-accounts/${accountId}/like`, { postId }),

  getFollowUpCommentAccounts: (params) =>
    api.get("/followup/comment-accounts", { params }),

  commentOnFollowUpAccount: (accountId, data) =>
    api.post(`/followup/comment-accounts/${accountId}/comment`, data),

  // ===== POST MANAGEMENT =====

  getPostDrafts: (params) => api.get("/posts/drafts", { params }),

  createPostDraft: (draftData) => api.post("/posts/drafts", draftData),

  updatePostDraft: (draftId, draftData) =>
    api.put(`/posts/drafts/${draftId}`, draftData),

  deletePostDraft: (draftId) => api.delete(`/posts/drafts/${draftId}`),

  publishPostDraft: (draftId, scheduleData) =>
    api.post(`/posts/drafts/${draftId}/publish`, scheduleData),

  getPostedPosts: (params) => api.get("/posts/published", { params }),

  // ===== MESSAGE MANAGEMENT =====

  getMessages: (params) => api.get("/messages", { params }),

  replyToMessage: (messageId, replyData) =>
    api.post(`/messages/${messageId}/reply`, replyData),

  markMessage: (messageId, status) =>
    api.patch(`/messages/${messageId}/status`, { status }),

  archiveMessage: (messageId) => api.patch(`/messages/${messageId}/archive`),

  getDirectMessages: (params) => api.get("/messages/direct", { params }),

  sendDirectMessage: (messageData) => api.post("/messages/direct", messageData),

  replyDirectMessage: (contactId, message) =>
    api.post(`/messages/direct/${contactId}/reply`, { message }),

  getConnectionMessages: (params) =>
    api.get("/messages/connections", { params }),

  sendConnectionRequest: (connectionData) =>
    api.post("/messages/connections/request", connectionData),

  acceptConnectionRequest: (connectionId) =>
    api.post(`/messages/connections/${connectionId}/accept`),

  declineConnectionRequest: (connectionId) =>
    api.post(`/messages/connections/${connectionId}/decline`),

  updateConnectionSettings: (settings) =>
    api.put("/messages/connections/settings", settings),

  // ===== AUTHENTICATION =====

  login: (credentials) => {
    console.log("🔥 Login API call to:", api.defaults.baseURL + "/auth/login");
    return api.post("/auth/login", credentials);
  },

  register: (userData) => {
    console.log(
      "🔥 Register API call to:",
      api.defaults.baseURL + "/auth/register",
    );
    return api.post("/auth/register", userData);
  },

  verifyLicense: (licenseKey) =>
    api.post("/auth/verify-license", { licenseKey }),

  // ===== HEALTH CHECK =====

  healthCheck: () => api.get("/health"),
};

export default apiService;
